import { CheckCircle, XCircle, Clock, HelpCircle, AlertCircle, FileCheck, UserCheck } from 'lucide-react';

export type AddressVerificationStatus =
  | 'unverified'
  | 'pending'
  | 'pending_approval'
  | 'pending_review'
  | 'verified'
  | 'rejected'
  | 'legacy';

export type AddressVerificationMethod = 'crowdsourced' | 'admin' | 'legacy' | null;

interface AddressVerificationBadgeProps {
  status: AddressVerificationStatus | null | undefined;
  method?: AddressVerificationMethod;
  verifiedAt?: string | null;
  submitterCount?: number | null;
  showLabel?: boolean;
  size?: 'sm' | 'md';
}

export function AddressVerificationBadge({
  status,
  method,
  verifiedAt,
  submitterCount,
  showLabel = true,
  size = 'sm',
}: AddressVerificationBadgeProps) {
  const effectiveStatus = status || 'unverified';

  const config: Record<AddressVerificationStatus, {
    icon: typeof CheckCircle;
    color: string;
    bg: string;
    label: string;
  }> = {
    verified: {
      icon: method === 'admin' ? UserCheck : CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-100',
      label: 'Verified',
    },
    pending_approval: {
      icon: FileCheck,
      color: 'text-amber-600',
      bg: 'bg-amber-100',
      label: 'Pending Approval',
    },
    pending_review: {
      icon: AlertCircle,
      color: 'text-orange-600',
      bg: 'bg-orange-100',
      label: 'Needs Review',
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
      label: 'Pending',
    },
    rejected: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-100',
      label: 'Rejected',
    },
    legacy: {
      icon: HelpCircle,
      color: 'text-purple-600',
      bg: 'bg-purple-100',
      label: 'Legacy',
    },
    unverified: {
      icon: HelpCircle,
      color: 'text-gray-500',
      bg: 'bg-gray-100',
      label: 'Unverified',
    },
  };

  const { icon: Icon, color, bg, label } = config[effectiveStatus];
  const iconSize = size === 'sm' ? 12 : 14;

  const badge = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${color}`}>
      <Icon size={iconSize} />
      {showLabel && label}
    </span>
  );

  // Build tooltip text
  let tooltipText = '';

  if (effectiveStatus === 'verified' && verifiedAt) {
    const date = new Date(verifiedAt);
    const formattedDate = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    if (method === 'crowdsourced' && submitterCount) {
      tooltipText = `Verified by ${submitterCount} users on ${formattedDate}`;
    } else if (method === 'admin') {
      tooltipText = `Manually verified by admin on ${formattedDate}`;
    } else if (method === 'legacy') {
      tooltipText = `Legacy address - needs re-verification`;
    } else {
      tooltipText = `Verified on ${formattedDate}`;
    }
  } else if (effectiveStatus === 'pending_approval' && submitterCount) {
    tooltipText = `${submitterCount} users confirmed this address. Awaiting admin approval.`;
  } else if (effectiveStatus === 'pending_review') {
    tooltipText = `Address verification failed geocoding. Needs admin review.`;
  } else if (effectiveStatus === 'pending' && submitterCount) {
    tooltipText = `${submitterCount} submission${submitterCount > 1 ? 's' : ''} received. Needs more confirmations.`;
  } else if (effectiveStatus === 'legacy') {
    tooltipText = `Legacy scraped address. Needs re-verification.`;
  } else if (effectiveStatus === 'rejected') {
    tooltipText = `Proposed address change was rejected by admin.`;
  }

  if (tooltipText) {
    return (
      <span title={tooltipText} className="cursor-help">
        {badge}
      </span>
    );
  }

  return badge;
}
